/**
 * Recognizing a backend that predates a field this frontend asks for.
 *
 * The two repos deploy independently, so there is always a window in which a
 * document selecting a newly added field reaches a backend whose schema does
 * not have it yet. An unknown field is a *validation* error: the whole
 * operation is refused and no data comes back at all, so a single new field
 * takes down every screen that shares the query. Recognizing that one failure
 * lets a caller retry with a document that omits the field, instead of
 * surfacing an error page for the entire page.
 */

interface GraphQlErrorLike {
  message?: string;
  extensions?: Record<string, unknown> | null;
}

/**
 * The GraphQL errors behind a failed operation, wherever Apollo put them.
 *
 * A validation failure is answered with HTTP 400, and HttpLink turns any
 * non-2xx response into a `ServerError` — so it arrives as `networkError` with
 * the parsed body hanging off `result`, not as `graphQLErrors`. Reading only
 * `graphQLErrors` would miss exactly the case this module exists for.
 */
export const graphQlErrorsOf = (error: unknown): GraphQlErrorLike[] => {
  const { graphQLErrors, networkError } =
    (error as {
      graphQLErrors?: GraphQlErrorLike[];
      networkError?: { result?: { errors?: GraphQlErrorLike[] } | null } | null;
    }) ?? {};

  if (graphQLErrors?.length) return graphQLErrors;

  const serverErrors = networkError?.result?.errors;
  if (serverErrors?.length) return serverErrors;

  // ApolloError concatenates the GraphQL error messages into `message`, so an
  // unrecognized shape still carries enough text to match on.
  return [{ message: (error as Error)?.message ?? '' }];
};

/**
 * True when the failure is the server refusing one of `fieldNames` as an
 * unknown field, rather than a network fault or a genuine server-side error.
 * Only that case is worth retrying: anything else fails the same way on a
 * document that merely drops the field, and swallowing it would hide a real
 * bug.
 */
export const isUnknownFieldError = (
  error: unknown,
  fieldNames: string[]
): boolean =>
  graphQlErrorsOf(error).some(({ message = '', extensions }) => {
    const isValidationFailure =
      extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
      /cannot query field/i.test(message);
    return isValidationFailure && fieldNames.some((f) => message.includes(f));
  });
